/**
 * Hook principal para gestionar el estado global de las pruebas
 * 
 * Centraliza toda la lógica de estado incluyendo navegación, validación,
 * respuestas y feedback. Coordina otros hooks especializados.
 * 
 * @returns {Object} Estado completo y funciones de la prueba
 * @author Ana Lizcano
 * @hook
 */


import { useState, useCallback, useEffect } from 'react';
import { loadBlockQuestions } from '../data/lazyQuestions';
import useTestNavigation from './useTestNavigation';
import useTestValidation from './useTestValidation';

const useTestState = () => {
  const [currentScreen, setCurrentScreen] = useState('welcome');
  const [testState, setTestState] = useState({
    currentBlock: 'A',
    currentQuestion: 0,
    responses: {
      blockA: Array(24).fill({}), // 24 preguntas con 4 características cada una
      blockB: Array(10).fill({}), // 10 preguntas de ranking con objetos
      blockC: Array(1).fill({}), // 1 pregunta con 4 canciones individuales + 2 pares de comparación
      blockD: Array(1).fill({}) // 1 pregunta de preferencias de imágenes
    },
    startTime: null,
    endTime: null
  });

  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);

  const [feedbackState, setFeedbackState] = useState({
    satisfaction: null,
    comments: ''
  });

  /**
   * Navega entre las diferentes pantallas de la aplicación
   */
  const navigateToScreen = useCallback((screen) => {
    setCurrentScreen(screen);
    if (screen === 'test' && !testState.startTime) {
      setTestState(prev => ({
        ...prev,
        startTime: new Date().toISOString()
      }));
    }
  }, [testState.startTime]);

  // Hooks especializados para funcionalidades específicas
  const validation = useTestValidation(testState);
  const navigation = useTestNavigation(testState, setTestState, navigateToScreen);
  const { navigateToBlock, navigateToQuestion, goToNextQuestion, goToPreviousQuestion, canGoPrevious } = navigation;

  /**
   * Guarda una respuesta en el estado
   */
  const saveResponse = useCallback((blockKey, questionIndex, response) => {
    setTestState(prev => {
      const newResponses = { ...prev.responses };
      const blockResponsesKey = `block${blockKey}`;
      
      newResponses[blockResponsesKey] = [...newResponses[blockResponsesKey]];
      newResponses[blockResponsesKey][questionIndex] = response;
      
      return {
        ...prev,
        responses: newResponses
      };
    });
  }, []);

  const handleGoToNextQuestion = useCallback(() => {
    goToNextQuestion(saveResponse);
  }, [goToNextQuestion, saveResponse]);

  const { getProgress, canGoNext, isTestCompleted } = validation;

  /**
   * Carga la pregunta actual cuando cambia el bloque o índice
   */
  useEffect(() => {
    const loadCurrentQuestion = async () => {
      setIsLoadingQuestion(true);
      try {
        const currentBlockKey = testState.currentBlock;
        const currentQuestionIndex = testState.currentQuestion;
        const currentBlockQuestions = await loadBlockQuestions(currentBlockKey);
        setCurrentQuestion(currentBlockQuestions[currentQuestionIndex] || null);
      } catch (error) {
        setCurrentQuestion(null);
      } finally {
        setIsLoadingQuestion(false);
      }
    };

    loadCurrentQuestion();
  }, [testState.currentBlock, testState.currentQuestion]);

  const getCurrentQuestion = useCallback(() => {
    return currentQuestion;
  }, [currentQuestion]);

  /**
   * Obtiene la respuesta actual del candidato
   */
  const getCurrentResponse = useCallback(() => {
    const currentBlockKey = testState.currentBlock;
    const currentQuestionIndex = testState.currentQuestion;
    const blockResponsesKey = `block${currentBlockKey}`;
    
    return testState.responses[blockResponsesKey][currentQuestionIndex];
  }, [testState.currentBlock, testState.currentQuestion, testState.responses]);

  /**
   * Limpia la respuesta actual según el tipo de bloque
   */
  const clearCurrentResponse = useCallback(() => {
    const currentBlockKey = testState.currentBlock;
    const currentQuestionIndex = testState.currentQuestion;
    const blockResponsesKey = `block${currentBlockKey}`;
    
    setTestState(prev => {
      const newResponses = { ...prev.responses };
      newResponses[blockResponsesKey] = [...newResponses[blockResponsesKey]];
      
      // Resetear la respuesta según el tipo de bloque
      if (currentBlockKey === 'A') {
        newResponses[blockResponsesKey][currentQuestionIndex] = {};
      } else if (currentBlockKey === 'B') {
        newResponses[blockResponsesKey][currentQuestionIndex] = {};
      } else if (currentBlockKey === 'C') {
        newResponses[blockResponsesKey][currentQuestionIndex] = {};
      } else if (currentBlockKey === 'D') {
        newResponses[blockResponsesKey][currentQuestionIndex] = {};
      }
      
      return {
        ...prev,
        responses: newResponses
      };
    });
  }, [testState.currentBlock, testState.currentQuestion]);

  /**
   * Actualiza el feedback del candidato
   */
  const updateFeedback = useCallback((field, value) => {
    setFeedbackState(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  return {
    // Estado
    currentScreen,
    testState,
    feedbackState,
    isLoadingQuestion,
    
    // Funciones de navegación
    navigateToScreen,
    navigateToBlock,
    navigateToQuestion,
    goToNextQuestion: handleGoToNextQuestion,
    goToPreviousQuestion,
    
    // Funciones de datos
    saveResponse,
    updateFeedback,
    getCurrentQuestion,
    getCurrentResponse,
    clearCurrentResponse,
    getProgress,
    
    // Funciones de validación
    canGoNext,
    canGoPrevious,
    isTestCompleted,
    
    // Constantes
    totalQuestions: getProgress.total
  };
};

export default useTestState;